import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { testimonials, type Testimonial } from "@/mocks/testimonials";

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-star-fill text-gold-DEFAULT text-xs" />
        </div>
      ))}
    </div>
  );
}

function TestimonialCard({
  testimonial,
  delay,
  isVisible,
}: {
  testimonial: Testimonial;
  delay: number;
  isVisible: boolean;
}) {
  return (
    <div
      className={`bg-white rounded-2xl p-7 flex flex-col h-full border border-cream-dark/80 transition-all duration-600 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between mb-5">
        <StarRating count={testimonial.rating} />
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-google-fill text-charcoal-soft text-xs" />
          </div>
          <span className="text-xs text-charcoal-soft/70 font-sans">
            {testimonial.source}
          </span>
        </div>
      </div>

      {/* Quote mark */}
      <div className="text-gold-DEFAULT/25 font-serif text-5xl leading-none mb-2 -mt-1">&ldquo;</div>

      <blockquote className="text-charcoal-soft font-sans text-sm leading-relaxed flex-1 mb-6 -mt-3">
        {testimonial.shortText || testimonial.text}
      </blockquote>

      <div className="flex items-center gap-3 mt-auto pt-4 border-t border-cream-dark">
        <div className="w-9 h-9 rounded-full bg-gold-DEFAULT/15 flex items-center justify-center flex-shrink-0">
          <span className="font-serif font-bold text-gold-dark text-base">
            {testimonial.name.charAt(0)}
          </span>
        </div>
        <div>
          <p className="font-bold text-charcoal text-sm font-sans">
            {testimonial.name}
          </p>
          <p className="text-xs text-charcoal-soft/60 font-sans">Verified Customer</p>
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: featuredRef, isVisible: featuredVisible } = useScrollReveal();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal();

  const featured = testimonials[activeIndex];

  return (
    <section className="bg-cream-DEFAULT py-20 md:py-28" aria-labelledby="testimonials-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div
          ref={headerRef}
          className={`mb-14 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-gold-DEFAULT" />
                <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
                  REAL CUSTOMERS
                </span>
              </div>
              <h2
                id="testimonials-heading"
                className="font-serif text-charcoal leading-tight max-w-lg"
                style={{ fontSize: "clamp(1.9rem, 3vw, 2.7rem)", lineHeight: 1.1 }}
              >
                What Our Customers Say About Us
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {[1, 2, 3, 4, 5].map((s) => (
                <div key={s} className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-gold-DEFAULT text-base" />
                </div>
              ))}
              <span className="text-charcoal font-bold font-sans text-sm ml-2 whitespace-nowrap">
                5.0 Average
              </span>
              <span className="text-charcoal-soft font-sans text-sm whitespace-nowrap">
                · 47+ Reviews
              </span>
            </div>
          </div>
        </div>

        {/* Featured Testimonial — elevated cinematic block */}
        <div
          ref={featuredRef}
          className={`relative bg-charcoal rounded-2xl overflow-hidden mb-6 transition-all duration-700 ${
            featuredVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Decorative gold corner accent */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-gold-DEFAULT/5 rounded-br-full" />
          <div className="absolute top-0 left-0 w-0.5 h-20 bg-gold-DEFAULT/40" />
          <div className="absolute top-0 left-0 h-0.5 w-20 bg-gold-DEFAULT/40" />

          <div className="relative z-10 p-8 md:p-12 flex flex-col lg:flex-row gap-10 items-start">
            <div className="flex-1 min-w-0">
              <div className="text-gold-DEFAULT font-serif text-7xl leading-none opacity-30 mb-2">
                &ldquo;
              </div>
              <blockquote className="text-white font-serif text-xl md:text-2xl leading-relaxed mb-8 -mt-4 italic">
                {featured.text}
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gold-DEFAULT/20 flex items-center justify-center flex-shrink-0 border border-gold-DEFAULT/30">
                  <span className="font-serif font-bold text-gold-DEFAULT text-xl">
                    {featured.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-bold text-white font-sans text-base">
                    {featured.name}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRating count={featured.rating} />
                    <span className="text-white/40 text-xs font-sans">
                      Verified {featured.source}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Reviewer selector */}
            <div className="flex flex-row lg:flex-col gap-2 flex-wrap flex-shrink-0">
              <p className="hidden lg:block text-white/30 text-xs font-sans tracking-widest uppercase mb-1 w-full">
                Switch Review
              </p>
              {testimonials.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold font-sans transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    i === activeIndex
                      ? "bg-gold-DEFAULT text-charcoal"
                      : "bg-white/8 text-white/50 hover:bg-white/15 hover:text-white/80"
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Card Grid */}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials.slice(0, 3).map((t, i) => (
            <TestimonialCard
              key={i}
              testimonial={t}
              delay={i * 90}
              isVisible={gridVisible}
            />
          ))}
        </div>

        {/* Post-testimonials CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://www.uagmn.com/testimonials/"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border-2 border-charcoal text-charcoal font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-charcoal hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Read All Reviews
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-external-link-line text-sm" />
            </div>
          </a>
          <a
            href="https://www.uagmn.com/inventory/"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gold-DEFAULT text-charcoal font-semibold text-sm px-8 py-3.5 rounded-full hover:bg-gold-light transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            View Inventory
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
